import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type { ByReferenceRequest } from "@/features/wallet/api/dto";

/**
 * Voie principale du worker ledger-service (`POST
 * /Transactions/mark-failed-by-reference`, guide v2.5 §6.3).
 *
 * Répercute un rejet comptable par référence — libère le blocage, passe en
 * FAILED. Idempotent par statut.
 * Rôles : `flashpay-service`, `flashpay-superadmin`, `flashpay-admin`.
 */
export const useMarkFailedByReference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "transactions", "markFailedByReference"],
    mutationFn: (body: ByReferenceRequest) =>
      mutateData({
        url: walletEndpoints.transactionsExt.markFailedByReference,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }),
    onSuccess: () =>
      invalidateAfterMutation(queryClient, walletTransactionCacheKeys()),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useMarkFailedByReference failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
